import React from 'react';
import { motion } from 'framer-motion';
import { Play, Rocket, Target, Trophy, Star, Users } from 'lucide-react';

const OurDrive: React.FC = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-primary to-cresento-dark text-white py-24 pt-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-cresento-light to-cresento-accent bg-clip-text text-transparent">
              Our Drive
            </h1>
            <p className="text-xl md:text-2xl text-cresento-light">
              The Journey That Fuels Our Passion to Democratize Sports Analytics
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Timeline */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-primary/30 via-cresento-dark/20 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Our Journey
            </h2>
            <p className="text-xl text-gray-300 mt-4 max-w-2xl mx-auto">
              From a simple observation to revolutionizing football analytics
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-8 sm:space-y-12">
            {/* The Observation */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-r from-cresento-primary/20 to-transparent p-6 sm:p-8 rounded-2xl border-l-4 border-cresento-accent">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cresento-secondary to-cresento-accent rounded-full flex items-center justify-center mr-4">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-cresento-accent">The Observation</h3>
                </div>
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Cresento was born from a simple observation: while professional athletes have access to advanced performance analytics, amateur and youth players are left behind. Our founders, passionate about both technology and football, saw an opportunity to <span className="text-cresento-accent font-semibold">bridge this gap</span>.
                </p>
              </div>
            </motion.div>

            {/* The Beginning */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-l from-cresento-primary/20 to-transparent p-6 sm:p-8 rounded-2xl border-r-4 border-cresento-secondary ml-auto">
                <div className="flex items-center mb-4 justify-end">
                  <h3 className="text-2xl font-bold text-cresento-secondary mr-4">The Beginning</h3>
                  <div className="w-12 h-12 bg-gradient-to-br from-cresento-accent to-cresento-secondary rounded-full flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                </div>
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed text-right">
                  Starting in the <span className="text-cresento-secondary font-semibold">HKUST Dream Builder Program</span>, we developed our first prototype that could track basic metrics. The response from players was overwhelming – they wanted more. This drove us to push the boundaries of what's possible, integrating AI to provide not just data, but <span className="text-cresento-accent font-semibold">actionable insights</span>.
                </p>
              </div>
            </motion.div>

            {/* The Breakthrough */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-r from-cresento-primary/20 to-transparent p-6 sm:p-8 rounded-2xl border-l-4 border-cresento-accent">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cresento-secondary to-cresento-accent rounded-full flex items-center justify-center mr-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-cresento-accent">The Breakthrough</h3>
                </div>
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Our breakthrough came when we realized that <span className="text-cresento-accent font-semibold">shin guards</span> – essential protective equipment worn by every player – could be the perfect platform for our technology. By embedding sensors and processors into something players already wear, we created a <span className="text-cresento-secondary font-semibold">seamless experience</span> that doesn't interfere with the game.
                </p>
              </div>
            </motion.div>

            {/* Today's Impact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-cresento-secondary/20 via-cresento-accent/20 to-cresento-primary/20 p-6 sm:p-8 rounded-2xl border border-cresento-secondary/50 text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cresento-accent to-cresento-secondary rounded-full flex items-center justify-center mr-4">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-cresento-accent">Today's Impact</h3>
                </div>
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed mb-4">
                  Today, after winning <span className="text-cresento-accent font-semibold">Unicorn Day</span> and partnering with elite clubs like <span className="text-cresento-secondary font-semibold">Inter Milan and Juventus</span>, we're proud to bring professional-level analytics to players at every level.
                </p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white">
                  From youth academies to weekend warriors, <span className="bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">Cresento is democratizing access to the tools that make champions</span>.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Drive Behind Our Mission */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-black to-cresento-primary/20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-8 sm:mb-12 bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              What Drives Us
            </h2>
            
            <div className="text-left space-y-6 text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Every young athlete deserves the chance to reach their full potential. Our drive comes from seeing the <span className="text-cresento-accent font-semibold">passion in players' eyes</span> when they discover what they're truly capable of.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                We're driven by the belief that <span className="text-cresento-secondary font-semibold">technology should democratize opportunity</span>, not create barriers. Every breakthrough we make is fueled by the vision of a world where talent, not access to resources, determines success.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white border-l-4 border-cresento-accent pl-6 italic"
              >
                Our drive is simple: <span className="bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">to turn every player into the athlete they dream of becoming</span>.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cresento-secondary to-cresento-accent">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4 text-white">Join Our Journey</h2>
            <p className="text-xl mb-8 text-gray-200">Be part of the drive to democratize sports analytics</p>
            <a 
              href="/level-up" 
              className="bg-white text-cresento-secondary hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center"
            >
              <Play className="mr-2 h-5 w-5" />
              Level Up Your Game
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OurDrive;